import { useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useMutation, useQuery } from "@apollo/client/react";
import { PublicSharePolicyDocument, UpdateSystemSettingsDocument } from "@/graphql/generated/operations";
import { Switch } from "@/components/design/Switch";
import { usePendingScope } from "@/lib/pending-edits";
import { LocalizedError } from "@/lib/localized-error";
import { userErrorMessage } from "@/lib/user-error";

export function PublicSharePolicy() {
  const intl = useIntl();
  const query = useQuery(PublicSharePolicyDocument, { fetchPolicy: "network-only" });
  const [change] = useMutation(UpdateSystemSettingsDocument);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef<Promise<void> | null>(null);
  const [draft, setDraft] = useState<boolean | null>(null);
  const stored = query.data?.systemSettings?.publicShareEnabled ?? false;
  const checked = draft ?? stored;
  const dirty = !!query.data && checked !== stored;
  usePendingScope("public-share-policy", { dirty, save, discard: () => { setDraft(null); setError(null); } });

  function save(): Promise<void> {
    if (inFlight.current) return inFlight.current;
    const pending = update(checked).finally(() => { inFlight.current = null; });
    inFlight.current = pending;
    return pending;
  }
  const label = intl.formatMessage({ id: "admin.groups.anonymous.publicShareEnabled" });

  async function update(required: boolean) {
    if (!dirty) return;
    setSaving(true);
    try {
      await change({ variables: { input: { data: { publicShareEnabled: required } } } });
      await query.refetch();
      setDraft(null);
      setError(null);
    } catch (cause) {
      const message = userErrorMessage(intl, cause);
      setError(message);
      throw new LocalizedError(message);
    } finally { setSaving(false); }
  }

  return <section className="mb-6 space-y-3" aria-label={label}>
    <div className="flex items-center justify-between gap-4">
      <label htmlFor="public-share-policy" className="text-smycles-body text-ink">{label}</label>
      <Switch id="public-share-policy" checked={checked}
        disabled={saving || query.loading || !query.data || !!query.error}
        onChange={setDraft} ariaLabel={label} />
    </div>
    <p className="text-smycles-meta text-ink-muted">{intl.formatMessage({ id: "admin.groups.anonymous.publicShareHint" })}</p>
    {(error || query.error) && <p role="alert" className="text-smycles-body text-ink">{error ?? userErrorMessage(intl, query.error)}</p>}
  </section>;
}
